import React, { useState } from "react";
import { money } from "@/lib/format";
import { num } from "@/lib/invoiceCalc";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { TpRowsEditor } from "@/components/invoice/TpRowsEditor";
import { Plus, Trash2, ChevronDown, ChevronUp } from "lucide-react";

function Section({ title, count, onAdd, children, testid }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="border-b border-slate-100 last:border-0 py-2.5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <button className="flex items-center gap-1 text-[15px] font-medium text-slate-800" onClick={() => setOpen(!open)}>{title}{count > 0 && (open ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />)}</button>
        <Button size="icon" variant="ghost" className="h-8 w-8 text-[#1A2A5E]" onClick={() => { onAdd(); setOpen(true); }} data-testid={`${testid}-add`}><Plus className="h-5 w-5" /></Button>
      </div>
      {open && count > 0 && <div className="space-y-2 mt-1.5">{children}</div>}
    </div>
  );
}

function PctRows({ rows, setRows, computed, withMode, testPrefix }) {
  const upd = (i, patch) => setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return rows.map((r, i) => (
    <div key={i} className="space-y-1" data-testid={`${testPrefix}-row-${i}`}>
      <div className="grid grid-cols-[1fr_64px_88px_32px] gap-2 items-center">
        <Input placeholder="Name" value={r.label} onChange={(e) => upd(i, { label: e.target.value })} className="h-8" data-testid={`${testPrefix}-label-${i}`} />
        <Input type="number" min="0" placeholder="%" value={r.percent} onChange={(e) => upd(i, { percent: e.target.value === "" ? "" : Math.max(0, num(e.target.value)) })} className="h-8 text-center" data-testid={`${testPrefix}-percent-${i}`} />
        <Input type="number" min="0" placeholder="₹" value={r.percent !== "" && r.percent != null ? (computed[i]?.amount ?? "") : r.amount}
          onChange={(e) => upd(i, { amount: Math.max(0, num(e.target.value)), percent: "" })} className="h-8 text-right" data-testid={`${testPrefix}-amount-${i}`} />
        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setRows(rows.filter((_, j) => j !== i))} data-testid={`${testPrefix}-delete-${i}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
      </div>
      {withMode && (
        <select value={r.mode || "before"} onChange={(e) => upd(i, { mode: e.target.value })} className="h-7 rounded-md border border-slate-200 text-xs px-1" data-testid={`${testPrefix}-mode-${i}`}>
          <option value="before">Before Tax</option><option value="after">After Tax</option>
        </select>
      )}
    </div>
  ));
}

export function AdjustmentsCard({ S, charges, setCharges, discounts, setDiscounts, tp, setTp, extras, setExtras, calc }) {
  const chComputed = calc.chRows;
  const discComputed = discounts.map((d, i) => calc.discRows.find((x) => x._idx === i) || {});
  const s = calc.summary;
  return (
    <Card className="p-4 border-slate-200">
      {S.charges && <Section title="Additional Charges" count={charges.length} onAdd={() => setCharges([...charges, { label: "", percent: "", amount: "" }])} testid="di-charges">
        <PctRows rows={charges} setRows={setCharges} computed={chComputed} testPrefix="di-charge" />
      </Section>}
      {S.discounts && <Section title="Add Discount (positive only)" count={discounts.length} onAdd={() => setDiscounts([...discounts, { label: "", percent: "", amount: "", mode: "before" }])} testid="di-discounts">
        <PctRows rows={discounts} setRows={setDiscounts} computed={discComputed} withMode testPrefix="di-discount" />
      </Section>}
      {S.tp && <Section title="Global TP (all items)" count={tp.length} onAdd={() => setTp([...tp, { narration: "", price: "", percent: "", mode: "before" }])} testid="di-global-tp">
        <TpRowsEditor rows={tp} onChange={setTp} withMode defaultBase={(r) => ((r.mode || "before") === "before" ? s.items_total : s.taxable_total + s.tax_total + s.item_tp_after)} testPrefix="di-gtp" />
      </Section>}
      {S.extras && <Section title="Extra Fields" count={extras.length} onAdd={() => setExtras([...extras, { label: "", value: "" }])} testid="di-extras">
        {extras.map((e2, i) => (
          <div key={i} className="grid grid-cols-[1fr_1fr_32px] gap-2 items-center">
            <Input placeholder="Label (e.g. Delivery Time)" value={e2.label} onChange={(e) => setExtras(extras.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} className="h-8" data-testid={`di-extra-label-${i}`} />
            <Input placeholder="Value (e.g. 15 Days)" value={e2.value} onChange={(e) => setExtras(extras.map((x, j) => j === i ? { ...x, value: e.target.value } : x))} className="h-8" data-testid={`di-extra-value-${i}`} />
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setExtras(extras.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4 text-red-500" /></Button>
          </div>
        ))}
      </Section>}
      <div className="grid grid-cols-[auto_1fr] gap-3 mt-3">
        <div className="rounded-lg bg-indigo-50 border border-indigo-100 px-4 py-2 text-[15px] font-medium text-[#1A2A5E] whitespace-nowrap" data-testid="di-total-qty">Total Qty: {s.total_qty}</div>
        <div className="rounded-lg bg-indigo-100 border border-indigo-200 px-4 py-2 flex items-center justify-between font-bold text-[#1A2A5E] text-base min-w-0">
          <span>Sub Total:</span><span className="font-mono" data-testid="di-subtotal">{money(s.raw)}</span>
        </div>
      </div>
    </Card>
  );
}
